import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useUserProfile } from '@/context/UserProfileContext';
import { useAppNavigation } from '@/lib/i18nNavigation';
import { cn } from '@/lib/utils';
import { signOut } from '@/utils/capacitorUtils';
import { IonIcon, IonImg, IonRouterLink } from '@ionic/react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { menuOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import GolfIcon from './GolfIcon';
import { Button } from './ui/button';
import { GolfAvatar } from './ui/GolfAvatar';

export function MainSidebar() {
  const { getCurrentPathname } = useAppNavigation();
  const pathname = getCurrentPathname();
  if (
    pathname.includes('/welcome') ||
    pathname.includes('/sign-in') ||
    pathname.includes('/sign-up') ||
    pathname.includes('/play') ||
    pathname.includes('/game')
  ) {
    return null;
  }

  return (
    <Sheet>
      <SheetContent side="left" className="p-0">
        <VisuallyHidden>
          <SheetTitle>
            Menu
          </SheetTitle>
        </VisuallyHidden>
        <SidebarContent />
      </SheetContent>

      <aside className="hidden h-screen md:flex md:flex-col">
        <SidebarContent />
      </aside>
    </Sheet>
  );
}

function SidebarContent() {
  const { getCurrentPathname } = useAppNavigation();
  const pathname = getCurrentPathname();
  const { t } = useTranslation();
  const { profile } = useUserProfile();

  if (!profile) {
    return "";
  }
  const menuItems = [
    { name: 'Home', iconName: 'home', href: '/dashboard' },
    { name: 'Stats', iconName: 'analytics', href: '/stats' },
    { name: 'Messages', iconName: 'message', href: '/messages', hasUnread: true },
    { name: 'Clubs', iconName: 'golf-hole', href: '/explore' },
    { name: 'Friends', iconName: 'user-multiple', href: '/friends' },
    { name: 'MyBag', iconName: 'golf-bat', href: '/my-bag' },
    { name: 'Notifications', iconName: 'notification', href: '/notifications' },
  ];

  // Check if the current pathname ends with '/messages'
  const isMessagesRoute = pathname.includes('/messages');
  const showLabel = true || !isMessagesRoute; // Determine if labels should be shown
  return (
    <div className={`flex h-full flex-col bg-card dark:bg-secondary ${showLabel ? 'xl:w-64' : 'w-16'}`}>
      <nav className="flex-1 space-y-2 px-2 py-7 h-screen-safe overflow-auto">
        <div className="flex flex-1 pl-2 pb-6 items-center gap-2">
          <Link to="/dashboard">
            <IonImg
              alt="logo"
              className="inline-block xl:hidden object-left size-8"
              src="/icon.svg"
            />
            <IonImg
              className="hidden object-left xl:inline-block h-6"
              alt="logo"
              src="/assets/images/logos/text-logo-green.svg"
            />
          </Link>
        </div>
        {/* <div className='hidden xl:inline-block w-full'>
          <GolfClubSearch mode="Search" />
        </div> */}
        {menuItems.map(item => (
          <Link
            key={item.name}
            to={item.href}
            replace
            className={cn(
              'flex items-center border-transparent p-3 border-l-4 text-foreground rounded-r-full font-medium hover:bg-secondary',
              pathname.endsWith(item.href)
                ? 'text-primary font-bold border-primary'
                : '',
            )}
          >
            <div className="flex flex-row items-center gap-3">
              <GolfIcon
                name={item.iconName}
                filled={pathname.endsWith(item.href)}
                themeAware={pathname.endsWith(item.href) === false}
                className='size-6'
              />
              {/* Red indicator for unread messages */}
              {item.hasUnread && pathname.endsWith(item.href) === false && (
                <span className="absolute top-0 right-0 size-2 rounded-full bg-red-500" />
              )}
              {/* Conditionally render text based on the current route */}
              {showLabel && (
                <span className="hidden xl:inline-block">{t(item.name.toLowerCase() as any)}</span>
              )}
            </div>
          </Link>
        ))}

        <Link
          to={'/u/' + profile?.username}
          replace
          className={cn(
            'flex items-center rounded-r-full p-3 font-medium hover:bg-secondary border-transparent border-l-4',
            pathname.endsWith('/u/' + profile?.username)
              ? 'text-primary font-bold border-primary'
              : '',
          )}
        >
          <div className="flex flex-row items-center gap-3">
            <GolfAvatar size={24} imageUrl={profile?.imageUrl} />
            {showLabel && (
              <span className="hidden xl:inline-block">{t('profile')}</span>
            )}
          </div>
        </Link>
      </nav>
      <div className="space-y-2 p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="flex w-full px-2 py-4 z-50 justify-center xl:justify-start items-center rounded-lg font-medium hover:bg-secondary">
              <IonIcon icon={menuOutline} className='size-6' />
              {showLabel && (
                <span className="hidden xl:inline-block">{t('more')}</span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{`${profile.firstName} ${profile.lastName}`}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {profile.primaryEmailAddress}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <IonRouterLink routerLink="/settings/edit-profile">
              <DropdownMenuItem>
                <GolfIcon name="user-edit" className="mr-2 size-4" />
                {t('edit_profile')}
              </DropdownMenuItem>
            </IonRouterLink>
            <IonRouterLink routerLink="/settings/display">
              <DropdownMenuItem>
                <GolfIcon name="settings" className="mr-2 size-4" />
                {t('settings')}
              </DropdownMenuItem>
            </IonRouterLink>
            <DropdownMenuSeparator />
            <IonRouterLink routerLink="/">
              <DropdownMenuItem>
                <button
                  type="button"
                  onClick={async () => signOut().then(() => history.push('/'))}
                  className="flex items-center my-1 gap-2 text-destructive"
                >
                  <GolfIcon name="sign-out" className="mr-2 size-4" />
                  <span>{t('signout')}</span>
                </button>
              </DropdownMenuItem>
            </IonRouterLink>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
